import React, { useState, useMemo, useEffect } from "react";
import {
  PaperAirplaneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { bulkSend, getBulkUploadTemplates } from "../api/documents";
import SendConfirmationModal from "./SendConfirmationModal";
import toast from "react-hot-toast";
import { useLoading } from "../contexts/LoadingContext";
import { InformationCircleIcon } from "@heroicons/react/16/solid";
import { useAuth } from "../contexts/AuthContext";
import { hasDocumentTypeAccess } from "../utils/permissions";
import { Button, Card, DataGrid, Dropdown } from "../components/ui";

const BulkSendForm = () => {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [csvError, setCsvError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [bulkUploadTemplates, setBulkUploadTemplates] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const { showLoader, hideLoader } = useLoading();
  const { permissions } = useAuth();

  // Filter templates based on document type permissions
  const filteredTemplates = useMemo(() => {
    if (!permissions || !permissions.templates) {
      return bulkUploadTemplates;
    }

    return bulkUploadTemplates.filter((template) => {
      const documentType = template.documentType || "";
      return hasDocumentTypeAccess(permissions, documentType);
    });
  }, [bulkUploadTemplates, permissions]);

  const ITEMS_PER_PAGE = 50;

  // Calculate pagination
  const totalPages = Math.ceil(recipients.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentRecipients = recipients.slice(startIndex, endIndex);

  const fetchBulkUploadTemplates = async () => {
    const response = await getBulkUploadTemplates();
    setBulkUploadTemplates(response);
  };

  useEffect(() => {
    fetchBulkUploadTemplates();
  }, []);

  const handleFileUpload = (event) => {
    setCsvError("");
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setCsvError("Please upload a valid CSV file");
      return;
    }

    setCsvFile(file);
    setCsvError("");
    setIsProcessing(true);
    setProcessingProgress(0);
    setCurrentPage(1);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split("\n");

        if (lines.length < 2) {
          setCsvError(
            "CSV file must contain at least a header row and one data row",
          );
          setIsProcessing(false);
          return;
        }

        // Parse header row
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
        const requiredHeaders = ["name", "role", "email"];

        // Check if all required headers are present
        const missingHeaders = requiredHeaders.filter(
          (h) => !headers.includes(h),
        );
        if (missingHeaders.length > 0) {
          setCsvError(
            `Missing required columns: ${missingHeaders.join(
              ", ",
            )}. Please include Name, Role, and Email columns.`,
          );
          setIsProcessing(false);
          return;
        }

        // Process data rows in chunks to avoid blocking UI
        const processChunk = (startLine, chunkSize) => {
          const endLine = Math.min(startLine + chunkSize, lines.length);
          const chunkRecipients = [];

          for (let i = startLine; i < endLine; i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(",").map((v) => v.trim());
              if (values.length >= 3) {
                const nameIndex = headers.indexOf("name");
                const roleIndex = headers.indexOf("role");
                const emailIndex = headers.indexOf("email");

                const recipient = {
                  name: values[nameIndex] || "",
                  role: values[roleIndex] || "",
                  email: values[emailIndex] || "",
                };

                // Validate that all required fields have values
                if (!recipient.name) {
                  setCsvError(
                    `Missing name value in row ${
                      i + 1
                    }. All fields are required.`,
                  );
                  setIsProcessing(false);
                  return;
                }

                if (!recipient.role) {
                  setCsvError(
                    `Missing role value in row ${
                      i + 1
                    }. All fields are required.`,
                  );
                  setIsProcessing(false);
                  return;
                }

                if (!recipient.email) {
                  setCsvError(
                    `Missing email value in row ${
                      i + 1
                    }. All fields are required.`,
                  );
                  setIsProcessing(false);
                  return;
                }

                // Basic email validation
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(recipient.email)) {
                  setCsvError(
                    `Invalid email format in row ${i + 1}: ${recipient.email}`,
                  );
                  setIsProcessing(false);
                  return;
                }

                chunkRecipients.push(recipient);
              } else {
                setCsvError(
                  `Row ${
                    i + 1
                  } has insufficient columns. Expected at least 3 columns (Name, Role, Email).`,
                );
                setIsProcessing(false);
                return;
              }
            }
          }

          return chunkRecipients;
        };

        // Process in chunks of 100 rows
        const CHUNK_SIZE = 100;
        let allRecipients = [];
        let currentLine = 1;

        const processNextChunk = () => {
          const chunk = processChunk(currentLine, CHUNK_SIZE);
          if (chunk) {
            allRecipients = allRecipients.concat(chunk);
            currentLine += CHUNK_SIZE;

            // Update progress
            const progress = Math.min((currentLine / lines.length) * 100, 100);
            setProcessingProgress(progress);

            if (currentLine < lines.length) {
              // Process next chunk in next tick to avoid blocking UI
              setTimeout(processNextChunk, 0);
            } else {
              // Finished processing
              if (allRecipients.length === 0) {
                setCsvError("No valid recipient data found in CSV file");
              } else {
                setRecipients(allRecipients);
              }
              setIsProcessing(false);
              setProcessingProgress(0);
            }
          }
        };

        processNextChunk();
      } catch (error) {
        setCsvError("Error parsing CSV file. Please check the format.");
        setIsProcessing(false);
        setProcessingProgress(0);
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowSendModal(true);
  };

  const handleSendConfirm = async (expiryDateTime) => {
    showLoader();

    try {
      await bulkSend({
        templateId: selectedTemplate,
        recipients: recipients,
        expiryDateTime: expiryDateTime,
      });

      toast.success("Bulk send initiated successfully!");
      setShowSendModal(false);
    } catch (error) {
      toast.error("Failed to initiate bulk send. Please try again.");
      setShowSendModal(false);
    } finally {
      hideLoader();
      setIsProcessing(false);
      setRecipients([]);
      setCsvFile(null);
      setCsvError("");
      setSelectedTemplate("");
    }
  };

  const downloadSampleCsv = () => {
    const csvContent =
      "Name,Role,Email\nJohn Doe,Signer,john@example.com\nJane Smith,CC,jane@example.com";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_recipients.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex items-center">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(endIndex, recipients.length)}
            </span>{" "}
            of <span className="font-medium">{recipients.length}</span>{" "}
            recipients
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="rounded-l-lg rounded-r-none px-2"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>

          {pages.map((page) => (
            <Button
              key={page}
              variant={page === currentPage ? "primary" : "outline"}
              size="sm"
              onClick={() => handlePageChange(page)}
              className="rounded-lg px-3"
            >
              {page}
            </Button>
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="rounded-r-lg rounded-l-none px-2"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  const templateOptions = filteredTemplates.map((t) => ({
    value: t._id,
    label: t.name,
  }));

  const recipientsColumns = [
    { key: "name", label: "Name", sortable: false },
    { key: "role", label: "Role", sortable: false },
    { key: "email", label: "Email", sortable: false },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-xl font-semibold text-gray-900 mb-2">
          Send Documents to Multiple Recipients
        </h2>
        <p className="text-gray-600">
          Upload a CSV file with recipient information and send documents to
          multiple recipients at once. Each recipient will receive their own
          copy of the document for signing.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Template Selection */}
        <div>
          <Dropdown
            label="Select Template"
            options={templateOptions}
            value={selectedTemplate}
            onChange={(val) => setSelectedTemplate(val)}
            placeholder="Choose a template..."
          />
          <div className="info-text bg-blue-50 rounded-lg p-2 mt-2 flex items-center">
            <p className="text-sm text-blue-800">
              <InformationCircleIcon className="h-5 w-5 inline-block mr-2" />
              Templates having only 1 signer can be used.
            </p>
          </div>
        </div>

        {/* CSV Upload */}
        <div>
          <label
            htmlFor="csvFile"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Upload Recipients CSV
          </label>
          <input
            type="file"
            id="csvFile"
            accept=".csv"
            onChange={handleFileUpload}
            className="block w-full px-4 py-3 border border-gray-300 hover:border-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg text-gray-900 text-sm transition-colors duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
            required
          />
          {csvError && <p className="mt-1 text-sm text-red-600">{csvError}</p>}
          {isProcessing && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-gray-600">
                  Processing CSV file...
                </span>
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${processingProgress}%` }}
                ></div>
              </div>
            </div>
          )}
          <div className="mt-2 flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={downloadSampleCsv}
              className="text-primary hover:text-primary-dark"
            >
              Download Sample CSV
            </Button>
            <span className="text-sm text-gray-500">
              Required columns: Name, Role, Email (all fields must have values)
            </span>
          </div>
        </div>

        {/* Recipients Preview */}
        {recipients.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Recipients ({recipients.length.toLocaleString()} total)
              </label>
              <span className="text-sm text-gray-500">
                Showing {startIndex + 1}-{Math.min(endIndex, recipients.length)}{" "}
                of {recipients.length.toLocaleString()}
              </span>
            </div>
            <Card padding="none" className="overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <DataGrid
                  columns={recipientsColumns}
                  data={currentRecipients.map((r, i) => ({
                    ...r,
                    id: startIndex + i,
                  }))}
                  headerVariant="white"
                  emptyMessage="No recipients"
                />
              </div>
              {renderPagination()}
            </Card>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={
              !selectedTemplate || recipients.length === 0 || isProcessing
            }
            leftIcon={isProcessing ? undefined : PaperAirplaneIcon}
          >
            {isProcessing ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Sending...</span>
              </>
            ) : (
              `Send to All Recipients (${recipients.length.toLocaleString()})`
            )}
          </Button>
        </div>
      </form>

      {/* Instructions */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h3 className="font-heading text-sm font-semibold text-blue-900 mb-2">
          How it works:
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Select a template from your saved templates</li>
          <li>• Upload a CSV file with Name, Role, and Email columns</li>
          <li>
            • All fields (Name, Role, Email) must have values for each row
          </li>
          <li>• Each recipient will receive a unique signing link</li>
          <li>• Track the progress of all documents in your dashboard</li>
          <li>• Large CSV files are processed efficiently with pagination</li>
        </ul>
      </div>

      {/* Send Confirmation Modal */}
      <SendConfirmationModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onConfirm={handleSendConfirm}
      />
    </div>
  );
};

export default BulkSendForm;
